import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Descriptions, Tag, Button, Typography } from "antd";
import { ChevronLeft, CalendarCheck, Clock, Receipt } from "lucide-react";
import { format } from "date-fns";
import { getBookingDetail } from "services/ordersAndBookings.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import { bookingStatusTag, formatDateTime, formatDate, InfoCard, CustomerCard } from "../commons/orders.helpers";
import PageLoader from "Components/PageLoader/PageLoader";
import { getBookingCodeDiscountLabel, getBookingTotal } from "../commons/orderTotals.helpers";

const { Title, Text } = Typography;

function BookingDetail() {
  const { bookingNumber } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, errors } = await getBookingDetail(bookingNumber);
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      setBooking(data);
    }
    load();
  }, [bookingNumber]);

  if (loading) return <PageLoader showHero={false} showTabs={false} showCards={false} rows={6} />;

  if (!booking) {
    return (
      <Row justify="center" align="middle" style={{ padding: "96px 0" }}>
        <Col style={{ textAlign: "center" }}>
          <CalendarCheck className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-3" />
          <Text strong style={{ display: "block", marginBottom: 8 }}>Booking not found</Text>
          <Button type="link" onClick={() => navigate(-1)}>Go back</Button>
        </Col>
      </Row>
    );
  }

  const cd = booking.customer_details;
  const pricing = booking.pricing;

  return (
    <div className="space-y-6">
      <Button
        type="text"
        icon={<ChevronLeft className="h-4 w-4" />}
        onClick={() => navigate(-1)}
      >
        Back to Bookings
      </Button>

      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 8]}>
        <Col xs={24} sm={16}>
          <Title level={3} style={{ margin: 0 }}>
            Booking <Text code>{booking.booking_number}</Text>
          </Title>
          <Row align="middle" gutter={8} style={{ marginTop: 4 }}>
            {booking.booking_type && (
              <Col>
                <Tag style={{ fontSize: 11 }}>{booking.booking_type}</Tag>
              </Col>
            )}
            {booking.booking_confirmed_at && (
              <Col>
                <Text type="secondary">
                  Confirmed on {format(new Date(booking.booking_confirmed_at), "PPP")}
                </Text>
              </Col>
            )}
          </Row>
        </Col>
        <Col xs={24} sm={8} style={{ textAlign: "right" }}>
          {bookingStatusTag(booking.booking_status)}
        </Col>
      </Row>

      {/* Customer + Booking details + Pricing */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <CustomerCard customer_details={cd} customer_id={booking.customer_id} />
        </Col>

        <Col xs={24} md={10}>
          <InfoCard title="Booking Details" icon={CalendarCheck}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Booking #" span={2}>
                <Text code style={{ fontSize: 12 }}>{booking.booking_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={2}>
                {bookingStatusTag(booking.booking_status)}
              </Descriptions.Item>
              <Descriptions.Item label="Qty">
                {booking.quantity ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {booking.booking_type ? (
                  <Tag style={{ fontSize: 11 }}>{booking.booking_type}</Tag>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Confirmed At" span={2}>
                {formatDateTime(booking.booking_confirmed_at)}
              </Descriptions.Item>
              {booking.order_number && (
                <Descriptions.Item label="Order #" span={2}>
                  <Text code style={{ fontSize: 12 }}>{booking.order_number}</Text>
                </Descriptions.Item>
              )}
              {booking.event_id && (
                <Descriptions.Item label="Event ID" span={2}>
                  <Text code style={{ fontSize: 12 }}>{booking.event_id}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </InfoCard>
        </Col>

        {pricing && (
          <Col xs={24} md={6}>
            <InfoCard title="Pricing" icon={Receipt}>
              <Row gutter={[0, 6]}>
                {pricing.unit_price != null && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col><Text type="secondary">Unit Price</Text></Col>
                      <Col><Text>{pricing.currency} {pricing.unit_price}</Text></Col>
                    </Row>
                  </Col>
                )}
                {pricing.discount > 0 && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col><Text type="secondary">Discount</Text></Col>
                      <Col><Text type="success">- {pricing.currency} {pricing.discount}</Text></Col>
                    </Row>
                  </Col>
                )}
                {getBookingCodeDiscountLabel(pricing) && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col><Text type="secondary">{getBookingCodeDiscountLabel(pricing)}</Text></Col>
                      <Col><Text type="success">- {pricing.currency} {pricing.code_discount}</Text></Col>
                    </Row>
                  </Col>
                )}
                {pricing.taxes > 0 && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col><Text type="secondary">Taxes</Text></Col>
                      <Col><Text>{pricing.currency} {pricing.taxes}</Text></Col>
                    </Row>
                  </Col>
                )}
                {pricing.total_price != null && (
                  <Col span={24} style={{ borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 2 }}>
                    <Row justify="space-between">
                      <Col><Text strong>Total</Text></Col>
                      <Col>
                        <Text strong style={{ color: "var(--primary)" }}>
                          {pricing.currency} {getBookingTotal(pricing)}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </InfoCard>
          </Col>
        )}
      </Row>

      {/* Product / Event info */}
      {(booking.product_details || booking.event_details) && (
        <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
          <div className="px-6 py-4 border-b border-border/50">
            <Title level={5} style={{ margin: 0 }}>Product / Event</Title>
          </div>
          <div className="px-6 py-4">
            <Row gutter={[16, 16]}>
              {booking.product_details?.name && (
                <Col xs={24} md={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}
                  >
                    Product
                  </Text>
                  <Text strong style={{ display: "block" }}>{booking.product_details.name}</Text>
                  {booking.product_details.description && (
                    <Text type="secondary">{booking.product_details.description}</Text>
                  )}
                  <Row gutter={4} style={{ marginTop: 6 }}>
                    {booking.product_details.product_type && (
                      <Col><Tag style={{ fontSize: 11 }}>{booking.product_details.product_type}</Tag></Col>
                    )}
                    {booking.product_details.product_domain && (
                      <Col><Tag color="blue" style={{ fontSize: 11 }}>{booking.product_details.product_domain}</Tag></Col>
                    )}
                    {booking.product_details.usage_type && (
                      <Col><Tag color="purple" style={{ fontSize: 11 }}>{booking.product_details.usage_type}</Tag></Col>
                    )}
                  </Row>
                </Col>
              )}
              {booking.event_details && (
                <Col xs={24} md={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}
                  >
                    Event
                  </Text>
                  <Text strong style={{ display: "block" }}>
                    {booking.event_details.event_name ?? booking.event_details.title ?? "—"}
                  </Text>
                  {booking.event_details.event_type && (
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                      {booking.event_details.event_type}
                    </Text>
                  )}
                  {booking.event_details.start_time && (
                    <Row align="middle" gutter={8} style={{ marginTop: 6 }}>
                      <Col>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </Col>
                      <Col>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {formatDateTime(booking.event_details.start_time)}
                          {booking.event_details.end_time &&
                            ` → ${formatDateTime(booking.event_details.end_time)}`}
                        </Text>
                      </Col>
                    </Row>
                  )}
                </Col>
              )}
            </Row>
          </div>
        </div>
      )}

      {/* Slot details */}
      {booking.slot_details && (
        <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
          <div className="px-6 py-4 border-b border-border/50">
            <Title level={5} style={{ margin: 0 }}>Slot Details</Title>
          </div>
          <div className="px-6 py-4">
            <Row gutter={[24, 8]}>
              {booking.slot_details.slot_date && (
                <Col xs={24} sm={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>Date</Text>
                  <Text strong>{formatDate(booking.slot_details.slot_date)}</Text>
                </Col>
              )}
              {booking.slot_details.start_time && (
                <Col xs={12} sm={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>Start</Text>
                  <Text strong>{formatDateTime(booking.slot_details.start_time)}</Text>
                </Col>
              )}
              {booking.slot_details.end_time && (
                <Col xs={12} sm={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>End</Text>
                  <Text strong>{formatDateTime(booking.slot_details.end_time)}</Text>
                </Col>
              )}
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingDetail;
